import { useSupabaseClient } from '@dolfin/hooks/supabase'
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from 'react-query'
import { showToastError, showToastSuccess } from '@dolfin/ui/messages'
import { Database } from '@dolfin/business/db_types'
import { DatabaseTableName, SupabaseTableHook } from '@dolfin/business/custom_types'
import { useCallback } from 'react'
import { useOrgContext } from './state/organization'
import { msg, t } from '@lingui/core/macro'
import { i18n, MessageDescriptor } from '@lingui/core'
import { buildQuery, ORG_FILTERS } from '@dolfin/utils/db'

interface SupabaseTableHookGenericProps {
  tableName: DatabaseTableName
  selectQuery?: string
  queryOptions?: Pick<
    UseQueryOptions<{ items: unknown[]; count: number | null }, unknown>,
    'retry' | 'retryDelay'
  >
  requestTimeoutMs?: number
}

const POSTGRES_UNIQUE_VIOLATION = '23505'

const DB_ERRORS: Record<string, MessageDescriptor> = {
  [POSTGRES_UNIQUE_VIOLATION]: msg`This item already exists and cannot be created again`,
  DST01: msg`Statement must be approved before it can be marked as paid`,
  DST02: msg`All of a statement's compensations need to be validated before it can be marked as paid`,
  DST04: msg`This statement has unreviewed compensation discrepancies that must be resolved or dismissed before approval`
}

type AnyTableInsert = Database['public']['Tables'][DatabaseTableName]['Insert']

const useSupabaseTable = <T, TInsert>(
  props: SupabaseTableHook<T> & SupabaseTableHookGenericProps
) => {
  const {
    tableName,
    selectQuery = '*',
    enabled = true,
    onSuccess,
    onError,
    setLoading,
    afterQuery,
    countItems,
    countType,
    head,
    afterInsert,
    afterUpdate,
    afterUpdateItems,
    afterUpsert,
    afterDelete,
    eqFilters,
    queryKeys,
    upsertOptions,
    notify = true,
    notifyError = true,
    refetchOnWindowFocus,
    refetchOnMount,
    skipOrgFilter,
    queryOptions,
    requestTimeoutMs
  } = props

  const selectedOrganizationId = useOrgContext(state => state.id)

  const supabase = useSupabaseClient<Database>()
  const queryClient = useQueryClient()

  const fetchItems = async (): Promise<{ items: T[]; count: number | null }> => {
    let query = supabase
      .from(tableName)
      .select(selectQuery, countItems ? { count: countType ?? 'exact', head } : undefined)

    query = buildQuery(
      query,
      {
        selectedOrganizationId: selectedOrganizationId!,
        tableName,
        skipOrgFilter
      },
      props
    )

    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const abortController = new AbortController()

    try {
      const result =
        requestTimeoutMs == null
          ? await query
          : await Promise.race([
              Promise.resolve(query.abortSignal(abortController.signal)).then(
                result => {
                  if (abortController.signal.aborted) {
                    throw new Error(`Query timed out after ${requestTimeoutMs}ms`)
                  }

                  return result
                },
                error => {
                  if (abortController.signal.aborted) {
                    throw new Error(`Query timed out after ${requestTimeoutMs}ms`)
                  }

                  throw error
                }
              ),
              new Promise<never>((_, reject) => {
                timeoutId = setTimeout(() => {
                  abortController.abort()
                  reject(new Error(`Query timed out after ${requestTimeoutMs}ms`))
                }, requestTimeoutMs)
              })
            ])
      const { data: items, error, count } = result

      if (error) throw error

      return { items: (items ?? []) as T[], count }
    } finally {
      if (timeoutId != null) {
        clearTimeout(timeoutId)
      }
    }
  }

  const {
    data: items,
    isError,
    isLoading,
    refetch
  } = useQuery(
    Array.isArray(queryKeys)
      ? [...(queryKeys ?? [tableName]), selectedOrganizationId]
      : `${tableName}-${selectedOrganizationId}`,
    fetchItems,
    {
      onError,
      onSuccess: (data: { items: T[]; count: number | null }) => {
        onSuccess?.(data)
        afterQuery?.(data)
      },
      enabled,
      refetchOnWindowFocus,
      refetchOnMount,
      ...queryOptions
    }
  )

  const getById = useCallback(
    async (id: number): Promise<T> => {
      const { data, error } = await supabase
        .from(tableName)
        .select(selectQuery)
        .eq('id', id)
        .single()

      if (error) throw error

      return data as T
    },
    [selectQuery, supabase, tableName]
  )

  const insertItem = useMutation(
    async (items: TInsert[]) => {
      if (selectedOrganizationId && ORG_FILTERS.includes(tableName) && !skipOrgFilter) {
        items = items.map(item => ({
          ...item,
          organization_id: selectedOrganizationId
        })) as TInsert[]
      }

      const query = supabase
        .from(tableName)
        .insert(items as AnyTableInsert[])
        .select(selectQuery)

      const { data, error } = await query

      if (error) throw error

      return data as T[]
    },
    {
      onSuccess: d => {
        setLoading?.(false)
        notify && showToastSuccess(t`Inserted successfully`)
        queryClient.invalidateQueries(queryKeys ?? [tableName])

        afterInsert?.(d as T[])
      },
      onError: (e: unknown) => {
        setLoading?.(false)
        const errorCode = typeof e === 'object' && e && 'code' in e ? String(e.code) : undefined
        notify &&
          showToastError(
            new Error(
              errorCode && errorCode in DB_ERRORS
                ? i18n._(DB_ERRORS[errorCode])
                : t`Something went wrong, please try again later or contact support`,
              { cause: e }
            )
          )
      }
    }
  )

  const updateItem = useMutation(
    async (
      item: Partial<T> & {
        id?: number | string | { [index: string]: number | string }
      }
    ) => {
      const { id, ...rest } = item
      let query = supabase.from(tableName).update(rest)

      if (typeof id === 'object') {
        Object.keys(id).forEach(field => (query = query.eq(field, id[field])))
      } else if (id) {
        query = query.eq('id', id)
      } else if (eqFilters) {
        eqFilters.forEach(filter => {
          query = query.eq(filter.column, filter.value)
        })
      }

      const { data, error } = await query.select(selectQuery)

      if (error) throw error

      return data[0] as T
    },
    {
      onSuccess: d => {
        setLoading?.(false)
        notify && showToastSuccess(t`Item updated`)
        queryClient.invalidateQueries(queryKeys ?? [tableName])

        afterUpdate?.(d)
      },
      onError: (e: unknown) => {
        setLoading?.(false)
        const errorCode = typeof e === 'object' && e && 'code' in e ? String(e.code) : undefined
        notify &&
          showToastError(
            new Error(
              errorCode && errorCode in DB_ERRORS
                ? i18n._(DB_ERRORS[errorCode])
                : t`Something went wrong updating the item, please try again later or contact support`,
              { cause: e }
            )
          )
        onError?.(e instanceof Error ? e : new Error('Unknown update error'))
      }
    }
  )

  const updateItems = useMutation(
    async ({ ids, update }: { ids: (number | string)[]; update: Partial<T> }) => {
      const { data, error } = await supabase
        .from(tableName)
        .update(update)
        .in('id', ids)
        .select(selectQuery)

      if (error) throw error

      return data as T[]
    },
    {
      onSuccess: d => {
        setLoading?.(false)
        notify && showToastSuccess(t`Items updated`)
        queryClient.invalidateQueries(queryKeys ?? [tableName])

        afterUpdateItems?.(d as T[])
      },
      onError: (e: unknown) => {
        setLoading?.(false)
        const errorCode = typeof e === 'object' && e && 'code' in e ? String(e.code) : undefined
        notify &&
          showToastError(
            new Error(
              errorCode && errorCode in DB_ERRORS
                ? i18n._(DB_ERRORS[errorCode])
                : t`Something went wrong updating the items, please try again later or contact support`,
              { cause: e }
            )
          )
      }
    }
  )

  const upsertItem = useMutation(
    async (items: (Partial<T> & { id?: number })[]) => {
      const { data, error } = await supabase
        .from(tableName)
        .upsert(items, upsertOptions)
        .select(selectQuery)

      if (error) throw error

      return data as T[]
    },
    {
      onSuccess: d => {
        setLoading?.(false)
        notify && showToastSuccess(t`Item updated`)
        queryClient.invalidateQueries(queryKeys ?? [tableName])

        afterUpsert?.(d)
      },
      onError: (e: unknown) => {
        setLoading?.(false)
        const errorCode = typeof e === 'object' && e && 'code' in e ? String(e.code) : undefined
        notify &&
          showToastError(
            new Error(
              errorCode && errorCode in DB_ERRORS
                ? i18n._(DB_ERRORS[errorCode])
                : t`Something went wrong, please try again later or contact support`,
              { cause: e }
            )
          )
      }
    }
  )

  const deleteItem = useMutation(
    async (id: number | string | (number | string)[] | { [index: string]: number | string }) => {
      let query = supabase.from(tableName).delete()

      if (typeof id === 'object' && !Array.isArray(id)) {
        Object.keys(id).forEach(field => (query = query.eq(field, id[field])))
      } else if (Array.isArray(id)) {
        query = query.in('id', id)
      } else {
        query = query.match({ id })
      }

      const { error } = await query
      if (error) throw error

      return id
    },
    {
      onSuccess: id => {
        setLoading?.(false)
        notify && notifyError && showToastSuccess(t`Item deleted`)
        queryClient.invalidateQueries(queryKeys ?? [tableName])

        afterDelete?.(id)
      },
      onError: (e: unknown) => {
        setLoading?.(false)
        const errorCode = typeof e === 'object' && e && 'code' in e ? String(e.code) : undefined
        notify &&
          showToastError(
            new Error(
              errorCode && errorCode in DB_ERRORS
                ? i18n._(DB_ERRORS[errorCode])
                : t`Something went wrong, please try again later or contact support`,
              { cause: e }
            )
          )
        onError?.(e instanceof Error ? e : new Error('Unknown delete error'))
      }
    }
  )

  const deleteJoinTableItem = useMutation(
    async (join: Record<string, string | number> | Record<string, string | number>[]) => {
      let query = supabase.from(tableName).delete()

      if (Array.isArray(join)) {
        join.forEach(j => (query = query.match(j)))
      } else query = query.match(join)

      const { error } = await query

      if (error) throw error

      return join
    },
    {
      onSuccess: join => {
        setLoading?.(false)
        notify && showToastSuccess(t`Item deleted`)
        queryClient.invalidateQueries(queryKeys ?? [tableName])

        // TODO: fix this
        afterDelete?.(join as T)
      },
      onError: (e: unknown) => {
        setLoading?.(false)
        const errorCode = typeof e === 'object' && e && 'code' in e ? String(e.code) : undefined
        notify &&
          showToastError(
            new Error(
              errorCode && errorCode in DB_ERRORS
                ? i18n._(DB_ERRORS[errorCode])
                : t`Something went wrong, please try again later or contact support`,
              { cause: e }
            )
          )
      }
    }
  )

  return {
    items: items?.items as T[],
    count: items?.count ?? null,
    fetchItems,
    getById,
    isError,
    isLoading,
    insertItem,
    updateItem,
    updateItems,
    upsertItem,
    deleteItem,
    deleteJoinTableItem,
    refetch
  }
}

export { useSupabaseTable }

export default useSupabaseTable
